import { Button } from '@rmwc/button';
import { CircularProgress } from '@rmwc/circular-progress';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@rmwc/dialog';
import { Select } from '@rmwc/select';
import { TextField } from '@rmwc/textfield';
import { Typography } from '@rmwc/typography';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';

import { intervalObjRev, queue, useAppState } from 'utilities';

import { IntervalEnum } from 'interfaces/enums';
import { AnalyticsGroupedResponseDto, AnalyticsRequestDto } from 'interfaces/interfaces';
import styles from './CustomModal.module.scss';

const intervalOptions = [
  {
    label: 'Default - Raw Interval',
    value: String(IntervalEnum.None),
  },
  {
    label: '5 Minute',
    value: String(IntervalEnum.FiveMinutes),
  },
  {
    label: '10 Minutes',
    value: String(IntervalEnum.TenMinutes),
  },
  {
    label: '15 Minutes',
    value: String(IntervalEnum.QuarterHourly),
  },
  {
    label: 'Half Hourly',
    value: String(IntervalEnum.HalfHourly),
  },
  {
    label: 'Hourly',
    value: String(IntervalEnum.Hourly),
  },
  {
    label: 'Daily',
    value: String(IntervalEnum.Daily),
  },
  {
    label: 'Weekly',
    value: String(IntervalEnum.Weekly),
  },
  {
    label: 'Monthly',
    value: String(IntervalEnum.Monthly),
  },
];

type ExportAnalyticsFormData = {
  startDate: string;
  endDate: string;
  interval: string;
  exportOption: string;
};

type ExportAnalyticsModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  additionalModalProps: {
    variables: {
      selectedData: { channelId: string; name: string; channelKey: string; reference?: string; utility: string }[];
      payload: AnalyticsRequestDto[];
    };
  };
};

export const ExportAnalyticsModal = ({ open, setOpen, additionalModalProps }: ExportAnalyticsModalProps) => {
  const { requestAnalytics } = useAppState();
  const { formState, control, handleSubmit, setError, clearErrors, reset } = useForm();
  const { errors } = formState;

  const selectedData = additionalModalProps.variables.selectedData;

  const escapeCsvCell = (value: unknown): string => {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = String(value);

    return /[",\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
  };

  // Raw table export
  const getRowsCsvData = (result: AnalyticsGroupedResponseDto[]) => {
    let csv = `Event,Load/Channel Name,Channel Key,Channel GUID,Reference,Utility Type,Unit,Usage\n`;

    result.forEach((item) => {
      const matchedChannel = selectedData.find((data) => data.channelId === item.id);

      if (!matchedChannel) {
        throw Error(
          'Oh oh! There was an error matching the results from the API to the select items. Please reload the page and try again'
        );
      }

      item.utilities.forEach((utility) => {
        utility.consumption.forEach((consumption) => {
          const dt = moment(consumption.event).format('DD/MM/YYYY HH:mm:ss');
          csv += `${escapeCsvCell(dt)},${escapeCsvCell(matchedChannel.name)},${escapeCsvCell(
            matchedChannel.channelKey
          )},${escapeCsvCell(matchedChannel.channelId)},${escapeCsvCell(matchedChannel.reference || '')},${escapeCsvCell(
            matchedChannel.utility
          )},${escapeCsvCell(utility.unit)},${escapeCsvCell(consumption.usage)}\n`;
        });
      });
    });

    return csv;
  };

  // Pivot table export
  const getColumnsCsvData = (result: AnalyticsGroupedResponseDto[]) => {
    const groupHash = {};
    let dates = [];

    let csv = `,${selectedData.map((x) => escapeCsvCell(x.name)).join(',')}\n`;
    csv += `Channel Key,${selectedData.map((x) => escapeCsvCell(x.channelKey)).join(',')}\n`;
    csv += `Channel GUID,${selectedData.map((x) => escapeCsvCell(x.channelId)).join(',')}\n`;
    csv += `Reference,${selectedData.map((x) => escapeCsvCell(x.reference || '')).join(',')}\n`;
    csv += `Event,${selectedData.map(() => '').join(',')}\n`;

    result.forEach((item) => {
      if (dates.length > 0) {
        return;
      }

      item.utilities.forEach((utility) => {
        if (dates.length === 0 && utility.consumption.length > 0) {
          dates = utility.consumption.map((consumption) => moment(consumption.event).format('DD/MM/YYYY HH:mm:ss'));
          return;
        }
      });
    });

    result.forEach((item) => {
      item.utilities.forEach((utility) => {
        if (utility.consumption.length === 0) {
          dates.forEach((dt) => {
            if (!groupHash[dt]) {
              groupHash[dt] = [];
            }

            groupHash[dt].push(null);
          });
        }

        utility.consumption.forEach((consumption, i) => {
          const dt = moment(consumption.event).format('DD/MM/YYYY HH:mm:ss');

          if (!groupHash[dt]) {
            groupHash[dt] = [];
          }

          groupHash[dt].push(consumption.usage || null);
        });
      });
    });

    for (const key in groupHash) {
      csv += key + ',' + groupHash[key].join(',') + '\n';
    }

    return csv;
  };

  const onSubmit = async (data: ExportAnalyticsFormData) => {
    /**
     * Used by default to export raw table data.
     */
    data.exportOption = data.exportOption || 'rawTable';

    try {
      const diff = moment(data.endDate, 'YYYY-MM-DD').diff(moment(data.startDate, 'YYYY-MM-DD'), 'days');
      if (diff > 732) {
        throw new Error('Maximum export time period is 2 years! Please adjust and retry.');
      }

      const start = moment(data.startDate, 'YYYY-MM-DD').format('YYYY-MM-DDT00:00:00');
      const end = moment(data.endDate, 'YYYY-MM-DD').format('YYYY-MM-DDT23:59:59');
      const interval = Number(data.interval);

      const requestParams = additionalModalProps.variables.payload.map((payload) => {
        return {
          ...payload,
          type: 4,
          start,
          end,
          interval,
        } as AnalyticsRequestDto;
      });

      const result = await requestAnalytics(requestParams);

      if (!result || result.length === 0) {
        throw new Error(
          'Failed to export data. Either there is no data, the date range is too long, or you have selected more than 1 cumulative channel.'
        );
      }

      const csvData = data.exportOption === 'rawTable' ? getRowsCsvData(result) : getColumnsCsvData(result);

      const intervalStr = intervalObjRev[interval];
      const exportType = data.exportOption === 'rawTable' ? 'Raw' : 'Pivot';
      const dt = moment(data.startDate, 'YYYY-MM-DD').format('DD-MM-YYYY');
      const dt2 = moment(data.endDate, 'YYYY-MM-DD').format('DD-MM-YYYY');
      const fileName = `ESP Analytics Export ${intervalStr} ${exportType} ${dt}-${dt2}.csv`;
      const link = document.createElement('a');

      link.setAttribute('href', `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(csvData)}`);
      link.setAttribute('download', fileName);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      queue.clearAll();
      queue.notify({
        icon: 'done',
        title: 'Success!',
        body: `Export should open shortly!`,
      });
      setOpen(false);
    } catch (ex) {
      queue.clearAll();
      queue.notify({
        icon: 'priority_high',
        title: 'Oh oh, error!',
        body: ex.message,
      });
      setError('api', { type: 'manual', message: ex.message });
    }
  };

  return (
    <div className={styles.customModal}>
      <Dialog
        className={styles.exportAnalytics}
        open={open}
        onClose={(evt) => {
          reset();
          clearErrors();
          if (open) {
            setOpen(false);
          }
        }}>
        <form onSubmit={handleSubmit(onSubmit)} data-private autoComplete="off">
          <DialogTitle>Export Analytics</DialogTitle>
          <DialogContent>
            <div className={styles.row}>
              <Controller
                name="startDate"
                control={control}
                rules={{
                  required: true,
                }}
                defaultValue={moment().startOf('day').subtract(1, 'month').format('YYYY-MM-DD')}
                render={({ field }) => (
                  <TextField
                    label="Start Date"
                    name="startDate"
                    type="date"
                    outlined
                    required
                    autoFocus
                    floatLabel={true}
                    {...field}
                  />
                )}
              />
              {errors.startDate && (
                <div className={styles.error}>
                  <Typography use="caption">Please enter a valid date DD/MM/YYYY</Typography>
                </div>
              )}
            </div>

            <div className={styles.row}>
              <Controller
                name="endDate"
                control={control}
                rules={{
                  required: true,
                }}
                defaultValue={moment().endOf('day').format('YYYY-MM-DD')}
                render={({ field }) => (
                  <TextField
                    label="End Date"
                    name="endDate"
                    type="date"
                    outlined
                    required
                    autoFocus
                    floatLabel={true}
                    {...field}
                  />
                )}
              />
              {errors.endDate && (
                <div className={styles.error}>
                  <Typography use="caption">Please enter a valid date DD/MM/YYYY</Typography>
                </div>
              )}
            </div>

            <div className={styles.row}>
              <Controller
                name="interval"
                control={control}
                defaultValue="0"
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Select label="Interval" outlined required options={intervalOptions} autoFocus {...field} />
                )}
              />
            </div>

            <div className={styles.row}>
              {errors.api && (
                <div className={styles.error} style={{ marginBottom: '16px' }}>
                  <Typography use="caption">{errors.api.message}</Typography>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'space-between' }}>
            <Button
              type="button"
              label="Close"
              disabled={formState.isSubmitting}
              outlined
              onClick={() => {
                setOpen(false);
                clearErrors();
                reset();
              }}
            />
            <Button
              raised
              disabled={formState.isSubmitting}
              label={formState.isSubmitting ? 'Exporting' : 'Export'}
              icon={formState.isSubmitting ? <CircularProgress theme="secondary" /> : null}
              onClick={() => {
                clearErrors();
              }}
              type="submit"
            />
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default ExportAnalyticsModal;
